import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import { BarChart3, Download, Loader2, TrendingUp, DollarSign, Package, Users } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, Legend } from 'recharts';
import { toast } from 'sonner';

export default function Reports() {
  const ctx = useOutletContext();
  const [stats, setStats] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('30d');

  useEffect(() => {
    Promise.all([adminApi.getDashboard(), adminApi.getReservations()])
      .then(([s, r]) => { setStats(s); setReservations(r); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>;

  const sourceData = reservations.reduce((acc, r) => {
    const s = r.how_heard || 'Unknown';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const sourceChart = Object.entries(sourceData).map(([name, value]) => ({ name, value }));
  const sourceColors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

  const trend = [
    { month: 'Jan', reservations: 8, revenue: 240, items: 64 },
    { month: 'Feb', reservations: 12, revenue: 360, items: 96 },
    { month: 'Mar', reservations: 18, revenue: 540, items: 144 },
    { month: 'Apr', reservations: 25, revenue: 750, items: 200 },
    { month: 'May', reservations: 32, revenue: 960, items: 256 },
  ];

  const exportReport = () => {
    const csv = ['Metric,Value',
      `Total Reservations,${stats.total_reservations}`,
      `Deposits Paid,${stats.deposits_paid}`,
      `Total Items,${stats.total_items}`,
      `Revenue,$${stats.revenue}`,
      `Outstanding,$${stats.outstanding_balances}`,
      `Conversion Rate,${stats.conversion_rate}%`,
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `report-${range}.csv`; a.click();
    toast.success('Report exported');
  };

  return (
    <div data-testid="admin-reports">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Reports & Analytics</h1>
          <p className={`text-sm ${ctx.textSecondary} mt-1`}>Business intelligence and trends</p>
        </div>
        <div className="flex gap-2">
          <select value={range} onChange={e => setRange(e.target.value)} className={`px-3 py-2 rounded-lg text-sm border ${ctx.borderColor} bg-transparent`} data-testid="reports-range">
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>
          <button onClick={exportReport} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700" data-testid="reports-export">
            <Download className="w-4 h-4" /> Export
          </button>
        </div>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total Revenue', value: `$${stats.revenue}`, icon: DollarSign, color: '#10b981', change: '+18%' },
          { label: 'New Reservations', value: stats.total_reservations, icon: TrendingUp, color: '#3b82f6', change: '+12%' },
          { label: 'Items Stored', value: stats.total_items, icon: Package, color: '#8b5cf6', change: '+9%' },
          { label: 'Conversion', value: `${stats.conversion_rate}%`, icon: Users, color: '#f59e0b', change: '+2.4%' },
        ].map((k, i) => {
          const Icon = k.icon;
          return (
            <div key={i} className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-4`}>
              <div className="flex items-center justify-between mb-2">
                <Icon className="w-5 h-5" style={{ color: k.color }} />
                <span className="text-xs font-medium text-emerald-400">{k.change}</span>
              </div>
              <p className="text-xl font-bold">{k.value}</p>
              <p className={`text-xs ${ctx.textSecondary}`}>{k.label}</p>
            </div>
          );
        })}
      </div>

      {/* Revenue trend */}
      <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-5 mb-6`}>
        <h3 className="text-sm font-semibold mb-4">Revenue & Reservations Trend</h3>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={trend}>
            <defs>
              <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/><stop offset="95%" stopColor="#10b981" stopOpacity={0}/></linearGradient>
              <linearGradient id="res" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/><stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/></linearGradient>
            </defs>
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: '#1e2030', border: 'none', borderRadius: 8, fontSize: 12 }} />
            <Area type="monotone" dataKey="revenue" stroke="#10b981" fill="url(#rev)" strokeWidth={2} />
            <Area type="monotone" dataKey="reservations" stroke="#3b82f6" fill="url(#res)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Marketing source */}
        <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-5`}>
          <h3 className="text-sm font-semibold mb-4">Marketing Source Attribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={sourceChart} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={3} dataKey="value">
                {sourceChart.map((entry, i) => <Cell key={i} fill={sourceColors[i % sourceColors.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: '#1e2030', border: 'none', borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Items stored */}
        <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-5`}>
          <h3 className="text-sm font-semibold mb-4">Monthly Items Stored</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={trend}>
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: '#1e2030', border: 'none', borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="items" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
